import {
  Box,
  Collapse,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import ErrorIcon from '@material-ui/icons/Error';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import InfoIcon from '@material-ui/icons/Info';
import clsx from 'clsx';
import {
  cloneElement,
  CSSProperties,
  FC,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { getTruncateMaxLinesStyle } from '../../styles';
import { WbExpandToggle } from '../WbExpandToggle';
import { WbButton } from '../WbButton';

export interface PreviewMessage {
  type: 'error' | 'warning' | 'info' | 'success';
  title: ReactNode;
  content: ReactNode;
}

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  success: {
    background: theme.palette.bkg.success,
    color: theme.palette.secondary.dark,
  },
  info: {
    background: theme.palette.bkg.info,
    color: theme.palette.secondary.dark,
  },
  error: {
    background: theme.palette.bkg.error,
    color: theme.palette.secondary.dark,
  },
  warning: {
    background: theme.palette.bkg.warning,
    color: theme.palette.secondary.dark,
  },
  compact: {
    gap: theme.spacing(1),
    '& $content': {
      ...getTruncateMaxLinesStyle(1),
    },
  },
  collapsible: {
    paddingInline: theme.spacing(1.5),
    paddingBlock: theme.spacing(1),
    alignItems: 'normal',
    borderRadius: '4px',
    flexDirection: 'column',
    boxShadow: theme.shadows[1],
  },
  default: {
    alignItems: 'normal',
    gap: '16px',
    margin: '12px 16px',
    borderRadius: '4px',
    flexDirection: 'column',
    boxShadow: theme.shadows[1],
  },
  titleCollapsible: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  warningIcon: {
    color: theme.palette.warning.main,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
  successIcon: {
    color: theme.palette.success.main,
  },
  infoIcon: {
    color: theme.palette.info.main,
  },
  expandIcon: {
    fontSize: '1.5rem',
  },
  content: {},
  closeIcon: {
    color: theme.palette.text.secondary,
    cursor: 'pointer',
  },
}));

interface Props {
  message: PreviewMessage;
  variant?: 'compact' | 'default' | 'collapsible';
  style?: CSSProperties;
  className?: string;
  closable?: boolean;
}

export const WbAlert: FC<Props> = ({
  message,
  style,
  variant = 'default',
  className,
  closable = false,
}) => {
  const classes = useStyles();

  const [expanded, setExpanded] = useState(true);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    setVisible(true);
  }, [message]);

  if (closable && !visible) return null;

  const classByType: Record<PreviewMessage['type'], string> = {
    info: classes.info,
    error: classes.error,
    warning: classes.warning,
    success: classes.success,
  };

  const classByVariant = {
    default: classes.default,
    compact: classes.compact,
    collapsible: classes.collapsible,
  };

  const iconByType: Record<PreviewMessage['type'], ReactNode> = {
    info: <InfoIcon className={classes.infoIcon} />,
    error: <ErrorIcon className={classes.errorIcon} />,
    warning: <ErrorOutlineIcon className={classes.warningIcon} />,
    success: <CheckCircleIcon className={classes.successIcon} />,
  };

  const variantClass = classByVariant[variant] ?? classByVariant.default;

  const content = (
    <Typography className={classes.content}>{message.content}</Typography>
  );

  const CloseButton = (
    <WbButton
      icon={<CloseIcon />}
      aria-label="Close error"
      variant="text"
      className={classes.closeIcon}
      onClick={() => setVisible(false)}
    />
  );

  let contentElement = content;

  if (variant === 'compact' && message.content)
    contentElement = <Tooltip title={message.content}>{content}</Tooltip>;

  if (variant === 'collapsible' && message.content)
    contentElement = <Collapse in={expanded}>{content}</Collapse>;

  let titleElement = (
    <Box className={classes.title}>
      <Typography style={{ fontWeight: 700 }}>{message.title}</Typography>
      {closable && CloseButton}
    </Box>
  );
  if (variant === 'compact')
    titleElement = (
      <Box className={classes.title}>
        <Typography style={{ fontWeight: 700 }}>
          {message.title}
          {variant === 'compact' && ':'}
        </Typography>
      </Box>
    );

  if (variant === 'collapsible')
    titleElement = (
      <Box className={classes.titleCollapsible}>
        {iconByType[message.type]}
        <Typography style={{ fontWeight: 700 }}>{message.title}</Typography>
        <WbExpandToggle
          style={{ marginLeft: 'auto', color: 'inherit' }}
          iconClassName={classes.expandIcon}
          expanded={expanded}
          onClick={() => setExpanded(old => !old)}
        />
        {closable && CloseButton}
      </Box>
    );

  return (
    <Box
      className={clsx(
        variantClass,
        classes.container,
        classByType[message.type],
        className,
      )}
      style={style}
    >
      {titleElement}
      {contentElement}
      {variant === 'compact' &&
        closable &&
        cloneElement(CloseButton, {
          style: { ...(CloseButton.props.style || {}), marginLeft: 'auto' },
        })}
    </Box>
  );
};
